import FileHelper from "@/helpers/utilities/FileHelper";

/**
 * Skobo AssignmentResource class representing a Teams assignment resource (eg file or link)
 * @category Models
 * @subcategory Education
 * @class Education
 * @name AssignmentResource
 */
class AssignmentResource {
  distributeForStudentWork = false;
  id = "";
  resource = {
    "@odata.type": "",
    displayName: "",
    link: "",
    fileUrl: null
  };

  // Skbo Specific
  isDirty = false;
  driveId = null;
  itemId = null;
  extension = null;
  tempId = null;

  constructor(graphFile) {
    if (graphFile) {
      if (graphFile.id) {
        this.itemId = graphFile.id;
      }

      if (graphFile.name) {
        this.resource.displayName = graphFile.name;
        this.extension = FileHelper.getFileExtensionObject(graphFile.name);
        this.resource["@odata.type"] = FileHelper.getFileOdataType(this.extension.type);
      }

      if (graphFile.file && graphFile.file.mimeType) {
        this.resource["@odata.type"] = FileHelper.getFileOdataType(graphFile.file.mimeType);
      }

      if (graphFile.parentReference) {
        this.driveId = graphFile.parentReference.driveId;
      }
    }
  }

  // Getters
  /**
   * Indicates if resource is a file
   * @returns {boolean} True if resource is a file
   */
  get isFileResource() {
    return (!!this.resource.fileUrl && this.resource.fileUrl !== "") || (!!this.driveId && !!this.itemId);
  }

  /**
   * Generate graph payload from resource
   * @returns {object} Payload object for graph api
   */
  get requestPayload() {
    if (!this.isFileResource) {
      return {
        distributeForStudentWork: false,
        resource: {
          "@odata.type": "#microsoft.graph.educationLinkResource",
          displayName: this.resource.displayName,
          link: this.resource.link
        }
      };
    } else {
      const payload = {
        distributeForStudentWork:
          this.resource["@odata.type"] === "#microsoft.graph.educationFileResource"
            ? false
            : this.distributeForStudentWork, // Only office files may be distributed
        resource: {
          "@odata.type": this.resource["@odata.type"],
          displayName: this.resource.displayName
        }
      };
      if (!payload.resource.fileUrl && this.driveId && this.itemId) {
        payload.resource.fileUrl =
          import.meta.env.VITE_APP_MSGRAPH_API_ENDPOINT + "/drives/" + this.driveId + "/items/" + this.itemId;
      }

      return payload;
    }
  }

  get canBeDistributedForStudentWork() {
    return (
      this.resource["@odata.type"] === "#microsoft.graph.educationWordResource" ||
      this.resource["@odata.type"] === "#microsoft.graph.educationExcelResource" ||
      this.resource["@odata.type"] === "#microsoft.graph.educationPowerPointResource"
    );
  }

  get unique() {
    if (this.id && this.id.length > 0) {
      return this.id;
    } else {
      return this.tempId;
    }
  }

  // Static Methods
  /**
   * Parse assignment resource from Graph Api to skobo
   * @param {object} graphAssignmentResource  Resource from graph api
   * @returns {object} Same resource object but parsed depending on being file or link
   */
  static parseFromGraph(graphAssignmentResource) {
    const resource = Object.assign(new AssignmentResource(), graphAssignmentResource);

    if (resource.isFileResource) {
      resource.extension = FileHelper.getFileExtensionObject(resource.resource.displayName);
    } else {
      resource.extension = FileHelper.getLinkTemplate();
    }
    return resource;
  }
}

export default AssignmentResource;
